import type { GunBarrelRequest, GunBarrelPoiLayer } from './types';
import type { TcSelectedDevArea } from './type-curve-filters/atoms';

const DEFAULT_SPACING_FT = 200;

/** The inventory gun barrel scope (`tcInventoryGunBarrelScopeAtom`). */
export interface InventoryGunBarrelScope {
  inventoryWorkspaceId: number;
  inventoryFormationPackageId: number | null;
  inventoryFormationPackageIds: number[];
}

export interface BuildGunBarrelRequestParams {
  selectedDevArea: TcSelectedDevArea | null;
  /** Non-null routes the request to the inventory_unit_slots backend layer. */
  inventoryScope: InventoryGunBarrelScope | null;
  structureGridCollectionId: number | null;
  typeCurveId: number;
  /** Type Curve page only — the inventory path carries no `targetSpacingFt`. */
  horizontalWellSpacingFt?: number | null;
  /**
   * Emphasised formation, sent as the slots layer's per-slot enrichment
   * `formationName`. NOT a server filter (the backend scopes by
   * `inventoryFormationPackageIds` + unit), so the page-owned inventory fetch
   * passes `null` and stays formation-agnostic — one call serves every
   * formation, and stepping the builder's arrows never refetches.
   */
  highlightedFormation?: string | null;
}

/**
 * Builds the `POST /queries/gun_barrel` request shared by the main-page
 * `TypeCurveGunBarrel` (Type Curve path) and the page-owned inventory fetch
 * (`useInventoryGunBarrel`). Returns `null` when there's no selected unit.
 *
 * @return {GunBarrelRequest | null} The request body, or `null`.
 */
export function buildGunBarrelRequest({
  selectedDevArea,
  inventoryScope,
  structureGridCollectionId,
  typeCurveId,
  horizontalWellSpacingFt,
  highlightedFormation,
}: BuildGunBarrelRequestParams): GunBarrelRequest | null {
  if (!selectedDevArea) return null;
  // Inventory Workspace: every configured zone's slots in one call — no per-zone
  // refetch — and no legacy `targetSpacingFt` (spacing is per zone). The plain
  // Type Curve page keeps the single-type-curve `insights_unit_formation_slots`
  // layer + `targetSpacingFt`.
  const slotsLayer: GunBarrelPoiLayer = inventoryScope
    ? {
        kind: 'inventory_unit_slots',
        inventoryWorkspaceId: inventoryScope.inventoryWorkspaceId,
        ...(inventoryScope.inventoryFormationPackageIds.length
          ? {
              inventoryFormationPackageIds:
                inventoryScope.inventoryFormationPackageIds,
            }
          : {}),
        ...(highlightedFormation
          ? { formationName: highlightedFormation }
          : {}),
      }
    : {
        kind: 'insights_unit_formation_slots',
        typeCurveHeaderDetailsId: typeCurveId,
      };
  return {
    query: {
      subject: {
        kind: 'development_area',
        // A clone's own id is synthetic and negative; it shares its source's
        // gun barrel until its boundary is edited. Real selections carry no
        // `sourceDevelopmentAreaId` and are unaffected.
        developmentAreaId:
          selectedDevArea.sourceDevelopmentAreaId ??
          selectedDevArea.developmentAreaId,
        // The Type Curve version whose scenario area config colours the bands.
        // Colour only: it is NOT `typeCurveHeaderDetailsId`, which the backend
        // also reads as an as-of geometry key (it renders the unit as it stood
        // at that version's promotion time). Sending the version here recolours
        // without moving a band.
        scenarioTypeCurveHeaderDetailsId: typeCurveId,
        ...(inventoryScope
          ? { inventoryWorkspaceId: inventoryScope.inventoryWorkspaceId }
          : {}),
        crossSectionEndPoints: selectedDevArea.crossSectionEndPoints,
      },
      poiLayers: [
        ...(structureGridCollectionId !== null
          ? [
              {
                kind: 'structure_grid_formations' as const,
                collectionIds: [structureGridCollectionId],
              },
            ]
          : []),
        { kind: 'wells' as const },
        slotsLayer,
      ],
      ...(inventoryScope
        ? {}
        : {
            targetSpacingFt: horizontalWellSpacingFt ?? DEFAULT_SPACING_FT,
          }),
    },
  };
}
